import React, { useEffect, useState } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  Button,
  useDisclosure,
  Center,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  VStack,
  Grid,
  GridItem,
  FormControl,
  FormLabel,
  ModalFooter,
  Text,
  HStack,
  Tooltip,
  IconButton,
  Select,
  useToast,
  Skeleton,
  SkeletonText,
} from "@chakra-ui/react";
import {
  addJobSkillsAsync,
  deleteJobSkillsAsync,
  fetchJobSkillsAsync,
  selectJobSkills,
  updateJobSkillsAsync,
} from "../../../JobPostingSlice/JobSkillsList/JobSkillsSlice";
import { useDispatch, useSelector } from "react-redux";
import { DeleteIcon, EditIcon } from "@chakra-ui/icons";

function ReqdSkills() {
  const {
    isOpen: isOpen1,
    onOpen: onOpen1,
    onClose: onClose1,
  } = useDisclosure();
  const {
    isOpen: isOpen2,
    onOpen: onOpen2,
    onClose: onClose2,
  } = useDisclosure();

  const dispatch = useDispatch();
  const toast = useToast();
  const jobSkills = useSelector(selectJobSkills);

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [newJobSkill, setNewJobSkill] = useState("");
  const [newJobStatus, setNewJobStatus] = useState("");
  const [error, setError] = useState(null);
  const [editJobSkill, setEditJobSkill] = useState("");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editJobStatus, setEditJobStatus] = useState("");
  const [editableJobSkill, setEditableJobSkill] = useState(null);
  const [deletingSkill, setDeletingSkill] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        await dispatch(fetchJobSkillsAsync());
      } catch (error) {
        toast({
          title: "Error loading data",
          description: "Failed to load job skills data",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [dispatch, toast]);

  const handleDelete = async (uuid) => {
    setIsDeleting(true);
    try {
      await dispatch(deleteJobSkillsAsync(uuid));
      await dispatch(fetchJobSkillsAsync());

      toast({
        title: "Success",
        description: "Job skill deleted successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });

      onClose2();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete job skill",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteModal = (job) => {
    setDeletingSkill(job);
    onOpen2();
  };

  const handleSave = async () => {
    if (!newJobSkill.trim() || !newJobStatus) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    const jobSkill = {
      title: newJobSkill,
      status: newJobStatus,
    };

    setIsSubmitting(true);
    try {
      await dispatch(addJobSkillsAsync(jobSkill));
      await dispatch(fetchJobSkillsAsync());

      toast({
        title: "Success",
        description: "Job skill added successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });

      setNewJobSkill("");
      setNewJobStatus("");
      onClose1();
    } catch (err) {
      setError(err.message);
      toast({
        title: "Error",
        description: "Failed to add job skill",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (jobSkill) => {
    setEditableJobSkill(jobSkill);
    setEditJobSkill(jobSkill.title);
    setEditJobStatus(jobSkill.status);
    setIsEditModalOpen(true);
  };

  const handleUpdate = async () => {
    if (editJobSkill.trim()) {
      setIsUpdating(true);
      try {
        await dispatch(
          updateJobSkillsAsync({
            uuid: editableJobSkill.uuid,
            jobSkillData: {
              title: editJobSkill,
              status: editJobStatus,
            },
          })
        );

        await dispatch(fetchJobSkillsAsync());

        toast({
          title: "Success",
          description: "Job skill updated successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });

        setEditableJobSkill(null);
        setEditJobSkill("");
        setEditJobStatus("");
        setIsEditModalOpen(false);
      } catch (err) {
        setError(err.message);
        toast({
          title: "Error",
          description: "Failed to update job skill",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
      } finally {
        setIsUpdating(false);
      }
    }
  };

  const TableSkeleton = () => (
    <>
      {[...Array(5)].map((_, index) => (
        <Tr key={index}>
          <Td>
            <Skeleton height="20px" />
          </Td>
          <Td>
            <Skeleton height="20px" />
          </Td>
          <Td>
            <Skeleton height="20px" />
          </Td>
          <Td>
            <HStack spacing={2}>
              <Skeleton height="32px" width="32px" />
              <Skeleton height="32px" width="32px" />
            </HStack>
          </Td>
        </Tr>
      ))}
    </>
  );

  return (
    <div className="min-h-screen bg-adminbg p-4 contentAdmin" style={{ fontFamily: "Inter, sans-serif" }}>
      <div className="">
        <div className="w-full my-5 bg-white rounded-md">
          <div className="flex justify-between">
            <p className="text-xl text-black font-semibold px-3 py-7">
              Skills List
            </p>
            <div className="py-4 px-3 flex gap-3">
              <Button
                colorScheme="blue"
                fontSize={"medium"}
                alignContent={"center"}
                onClick={onOpen1}
              >
                Add<i className="fa-solid fa-plus px-2"></i>
              </Button>

              <Modal isOpen={isOpen1} onClose={onClose1} size="4xl">
                <ModalOverlay />
                <ModalContent>
                  <ModalHeader bg="blue.500" color="white" borderTopRadius="md">
                    Add Skills
                  </ModalHeader>
                  <ModalCloseButton color="white" />
                  <ModalBody py={6}>
                    <VStack spacing={6}>
                      <Grid templateColumns="repeat(2, 1fr)" gap={4} width="100%">
                        <GridItem colSpan={2}>
                          <FormControl>
                            <FormLabel>Job Skill</FormLabel>
                            <Input
                              className=" "
                              color="black"
                              placeholder="Enter Job Skill"
                              value={newJobSkill}
                              onChange={(e) => setNewJobSkill(e.target.value)}
                            />
                          </FormControl>
                        </GridItem>
                        <GridItem colSpan={2}>
                          <FormControl>
                            <FormLabel>Job Status</FormLabel>
                            <Select
                              placeholder="Select Job Status"
                              value={newJobStatus}
                              onChange={(e) => setNewJobStatus(e.target.value)}
                              borderColor="black"
                              color="black"
                            >
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                            </Select>
                          </FormControl>
                        </GridItem>
                      </Grid>
                      {error && <Text color="red.500">{error}</Text>}
                    </VStack>
                  </ModalBody>
                  <ModalFooter bg="gray.50" borderBottomRadius="md">
                    <Button
                      colorScheme="blue"
                      mr={3}
                      onClick={handleSave}
                      isLoading={isSubmitting}
                      loadingText="Saving..."
                    >
                      Save
                    </Button>
                    <Button
                      onClick={onClose1}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                  </ModalFooter>
                </ModalContent>
              </Modal>

              <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} size="4xl">
                <ModalOverlay />
                <ModalContent>
                  <ModalHeader bg="blue.500" color="white" borderTopRadius="md">
                    Edit Skills
                  </ModalHeader>
                  <ModalCloseButton color="white" />
                  <ModalBody py={6}>
                    <VStack spacing={6}>
                      <Grid templateColumns="repeat(2, 1fr)" gap={4} width="100%">
                        <GridItem colSpan={2}>
                          <FormControl>
                            <FormLabel>Job Skill</FormLabel>
                            <Input
                              className=" "
                              color="black"
                              placeholder="Enter job skill"
                              value={editJobSkill}
                              onChange={(e) => setEditJobSkill(e.target.value)}
                            />
                          </FormControl>
                        </GridItem>
                        <GridItem colSpan={2}>
                          <FormControl>
                            <FormLabel>Job Status</FormLabel>
                            <Select
                              placeholder="Select job status"
                              value={editJobStatus}
                              onChange={(e) => setEditJobStatus(e.target.value)}
                              borderColor="black"
                              color="black"
                            >
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                            </Select>
                          </FormControl>
                        </GridItem>
                      </Grid>
                      {error && <Text color="red.500">{error}</Text>}
                    </VStack>
                  </ModalBody>
                  <ModalFooter bg="gray.50" borderBottomRadius="md">
                    <Button
                      colorScheme="blue"
                      mr={3}
                      onClick={handleUpdate}
                      isLoading={isUpdating}
                      loadingText="Updating..."
                    >
                      Update
                    </Button>
                    <Button
                      onClick={() => setIsEditModalOpen(false)}
                      disabled={isUpdating}
                    >
                      Cancel
                    </Button>
                  </ModalFooter>
                </ModalContent>
              </Modal>

              <Modal isOpen={isOpen2} onClose={onClose2}>
                <ModalOverlay />
                <ModalContent maxW="2xl" borderradius="lg" boxShadow="lg">
                  <ModalHeader fontSize="lg" fontWeight="bold" textAlign="center">
                    Confirm Deletion
                  </ModalHeader>
                  <ModalCloseButton />
                  <ModalBody>
                    <Text fontSize="md" textAlign="center">
                      Are you sure you want to delete{" "}
                      <Text as="span" fontWeight="semibold" color="red.500">
                        "{deletingSkill?.title}"
                      </Text>
                      ?
                    </Text>
                  </ModalBody>
                  <ModalFooter display="flex" gap="2" justifyContent="center">
                    <Button
                      colorScheme="red"
                      w="40"
                      onClick={() => handleDelete(deletingSkill.uuid)}
                      isLoading={isDeleting}
                      loadingText="Deleting..."
                    >
                      Delete
                    </Button>
                    <Button
                      variant="outline"
                      colorScheme="red"
                      w="40"
                      onClick={onClose2}
                      disabled={isDeleting}
                    >
                      Cancel
                    </Button>
                  </ModalFooter>
                </ModalContent>
              </Modal>
            </div>
          </div>

          <TableContainer>
            <Table size="sm">
              <Thead className="bg-gray-200">
                <Tr>
                  <Th className="w-1/4">uuid</Th>
                  <Th className="w-1/4">Title</Th>
                  <Th className="w-1/4">Status</Th>
                  <Th className="w-1/4">Action</Th>
                </Tr>
              </Thead>
              <Tbody>
                {isLoading ? (
                  <TableSkeleton />
                ) : jobSkills && jobSkills.length > 0 ? (
                  [...jobSkills]
                    .sort((a, b) => a.uuid - b.uuid)
                    .map((job, index) => (
                      <Tr key={job.uuid}>
                        <Td>{index + 1}</Td>
                        <Td>{job.title}</Td>
                        <Td textColor={"green"}>{job.status}</Td>
                        <Td>
                          <HStack spacing={2}>
                            <Tooltip label="Edit Skill" placement="top">
                              <IconButton
                                icon={<EditIcon />}
                                colorScheme="blue"
                                size="sm"
                                onClick={() => handleEdit(job)}
                                aria-label="Edit skill"
                              />
                            </Tooltip>
                            <Tooltip label="Delete Skill" placement="top">
                              <IconButton
                                icon={<DeleteIcon />}
                                colorScheme="red"
                                size="sm"
                                onClick={() => openDeleteModal(job)}
                                aria-label="Delete skill"
                              />
                            </Tooltip>
                          </HStack>
                        </Td>
                      </Tr>
                    ))
                ) : (
                  <Tr>
                    <Td colSpan="4" className="text-center py-4">
                      No data available
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </div>
      </div>
    </div>
  );
}

export default ReqdSkills;